import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import axios from 'axios';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { FloatInput } from 'ui-components/components/float-input';
import { Text, TextType } from 'ui-components/components/text';
import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { StateDropdown } from '../../../../../../address/state-dropdown';
import { CountryDropdown } from '../../../../../../address/country-dropdown';
import { CustomerAutoComplete } from '../../../../../../components/customer-auto-complete';
import type { Customer } from '@shipstation/client-common/src/types/customer';
import type { LtlAddress } from '@shipstation/client-common/types/ltl';
import { useFreightQuote } from '../../freight-quote-context';

import style from './ship-to-step.module.less';

const shipToStepMessages = defineMessages({
  nameLabel: {
    id: 'zdRRMT',
    description: 'Name field label in ship-to step',
    defaultMessage: 'Name',
  },
  companyLabel: {
    id: 'n2Iqc+',
    description: 'Company field label in ship-to step (optional)',
    defaultMessage: 'Company (optional)',
  },
  countryLabel: {
    id: 'udBNOu',
    description: 'Country field label in ship-to step',
    defaultMessage: 'Country',
  },
  addressLine1Label: {
    id: 'LYF+DZ',
    description: 'Address Line 1 field label in ship-to step',
    defaultMessage: 'Address Line 1',
  },
  addressLine2Label: {
    id: 'arbMvp',
    description: 'Address Line 2 field label in ship-to step (optional)',
    defaultMessage: 'Address Line 2 (optional)',
  },
  cityLabel: {
    id: '9Co0GI',
    description: 'City field label in ship-to step',
    defaultMessage: 'City',
  },
  stateLabel: {
    id: 'FW19Yn',
    description: 'State field label in ship-to step',
    defaultMessage: 'State',
  },
  zipCodeLabel: {
    id: 'HjGbL3',
    description: 'Zip Code field label in ship-to step',
    defaultMessage: 'Zip Code',
  },
  phoneLabel: {
    id: 'UtVbcH',
    description: 'Phone field label in ship-to step (optional)',
    defaultMessage: 'Phone (optional)',
  },
  emailLabel: {
    id: 'iGe3v1',
    description: 'Email field label in ship-to step (optional)',
    defaultMessage: 'Email (optional)',
  },
  saveAsNewCustomer: {
    id: 'vVJsqA',
    description: 'Save as new customer checkbox label in ship-to step',
    defaultMessage: 'Save as new customer',
  },
  deliveryServicesLabel: {
    id: '67p3gX',
    description: 'Delivery services section label in ship-to step',
    defaultMessage: 'Delivery Services',
  },
  insideDelivery: {
    id: '5s+Kj5',
    description: 'Inside Delivery service checkbox label',
    defaultMessage: 'Inside Delivery',
  },
  liftgateDelivery: {
    id: 'j8JyLU',
    description: 'Liftgate Delivery service checkbox label',
    defaultMessage: 'Liftgate Delivery',
  },
  residentialDelivery: {
    id: 'h10Pfk',
    description: 'Residential Delivery service checkbox label',
    defaultMessage: 'Residential Delivery',
  },
  constructionSiteDelivery: {
    id: 'I/y4jO',
    description: 'Construction Site Delivery service checkbox label',
    defaultMessage: 'Construction Site Delivery',
  },
  notifyBeforeDelivery: {
    id: 'liRYaA',
    description: 'Notify Before Delivery service checkbox label',
    defaultMessage: 'Notify Before Delivery',
  },
  holdAtTerminal: {
    id: 'pqUNYi',
    description: 'Hold Shipment at Terminal service checkbox label',
    defaultMessage: 'Hold Shipment at Terminal',
  },
  appointmentDelivery: {
    id: 'w2NpC5',
    description: 'Appointment Delivery service checkbox label',
    defaultMessage: 'Appointment Delivery',
  },
  tradeShowDelivery: {
    id: 'pXZTcw',
    description: 'Tradeshow Delivery service checkbox label',
    defaultMessage: 'Tradeshow Delivery',
  },
  saveButton: {
    id: 'wpdKmg',
    description: 'Save button in ship-to step',
    defaultMessage: 'Save',
  },
});

type ShipToStepProps = {
  onSave: () => void;
};

export const ShipToStepSummary = () => {
  const { state } = useFreightQuote();
  const dest = state.destinationAddress;
  const services = state.additionalServices;

  const activeServices = [
    services.isInsideDelivery && 'Inside Delivery',
    services.isLiftgateDelivery && 'Liftgate Delivery',
    services.isResidentialDelivery && 'Residential Delivery',
    services.isConstructionSiteDelivery && 'Construction Site Delivery',
    services.isNotifyBeforeDelivery && 'Notify Before Delivery',
    services.isAppointmentDelivery && 'Appointment Delivery',
    services.isTradeShowDelivery && 'Tradeshow Delivery',
  ].filter(Boolean) as string[];

  const addressParts = [dest.line1, dest.city, dest.state, dest.postalCode].filter(Boolean).join(', ');

  return (
    <FlexColumn gap={Gap.Space4}>
      {dest.name && <Text textType={TextType.Caption} value={dest.name} />}
      {dest.company && <Text textType={TextType.Caption} value={dest.company} />}
      {addressParts && <Text textType={TextType.Caption} value={addressParts} />}
      {dest.phone && <Text textType={TextType.Caption} value={dest.phone} />}
      {activeServices.length > 0 && (
        <FlexRow className={style['service-tags']} gap={Gap.Space4}>
          {activeServices.map((svc) => (
            <span className={style['service-tag']} key={svc}>
              {svc}
            </span>
          ))}
        </FlexRow>
      )}
    </FlexColumn>
  );
};

export const ShipToStep = ({ onSave }: ShipToStepProps) => {
  const intl = useIntl();
  const { state, localDispatch } = useFreightQuote();
  const dest: LtlAddress = state.destinationAddress;
  const services = state.additionalServices;
  const [customerSuggestions, setCustomerSuggestions] = useState<Customer[]>([]);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!dest.countryCode) {
      localDispatch({
        type: 'UPDATE_DESTINATION_ADDRESS',
        address: { ...dest, countryCode: 'US' },
      });
    }
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleField = useCallback(
    (field: keyof LtlAddress, value: string) => {
      localDispatch({
        type: 'UPDATE_DESTINATION_ADDRESS',
        address: { ...dest, [field]: value },
      });
    },
    [localDispatch, dest],
  );

  const handleNameChange = useCallback(
    (value: string) => {
      handleField('name', value);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (value.length >= 3) {
        searchTimerRef.current = setTimeout(async () => {
          try {
            const response = await axios.get(`/customer/autocomplete/${encodeURIComponent(value)}`);
            setCustomerSuggestions(response.data ?? []);
          } catch {
            setCustomerSuggestions([]);
          }
        }, 400);
      } else {
        setCustomerSuggestions([]);
      }
    },
    [handleField],
  );

  const handleCustomerSelected = useCallback(
    (index: number) => {
      const customer = customerSuggestions[index];
      if (!customer) return;
      localDispatch({
        type: 'UPDATE_DESTINATION_ADDRESS',
        address: {
          ...dest,
          name: customer.name ?? '',
          company: customer.company ?? '',
          line1: customer.line1 ?? '',
          line2: customer.line2 ?? '',
          city: customer.city ?? '',
          state: customer.state ?? '',
          postalCode: customer.postalCode ?? '',
          countryCode: customer.countryCode || dest.countryCode || 'US',
          phone: customer.phone ?? '',
          email: customer.email ?? '',
        },
      });
      setCustomerSuggestions([]);
    },
    [customerSuggestions, dest, localDispatch],
  );

  const handleServiceToggle = useCallback(
    (field: string, checked: boolean) => {
      localDispatch({
        type: 'UPDATE_ADDITIONAL_SERVICES',
        additionalServices: { ...services, [field]: checked },
      });
    },
    [localDispatch, services],
  );

  return (
    <FlexColumn gap={Gap.Space12}>
      <div className={style['name-field-wrapper']}>
        <FloatInput
          label={intl.formatMessage(shipToStepMessages.nameLabel)}
          onBlur={() => setTimeout(() => setCustomerSuggestions([]), 150)}
          onChange={handleNameChange}
          type='text'
          value={dest.name}
        />
        {customerSuggestions.length > 0 && (
          <CustomerAutoComplete customers={customerSuggestions} onRowClick={handleCustomerSelected} path='' />
        )}
      </div>

      <FloatInput
        label={intl.formatMessage(shipToStepMessages.companyLabel)}
        onChange={(value: string) => handleField('company', value)}
        type='text'
        value={dest.company}
      />

      <div>
        <Text textType={TextType.Label} value={<FormattedMessage {...shipToStepMessages.countryLabel} />} />
        <CountryDropdown
          componentClassName={style['dropdown-full']}
          onChange={(value: string) => handleField('countryCode', value)}
          value={dest.countryCode}
        />
      </div>

      <FloatInput
        label={intl.formatMessage(shipToStepMessages.addressLine1Label)}
        onChange={(value: string) => handleField('line1', value)}
        type='text'
        value={dest.line1}
      />

      <FloatInput
        label={intl.formatMessage(shipToStepMessages.addressLine2Label)}
        onChange={(value: string) => handleField('line2', value)}
        type='text'
        value={dest.line2}
      />

      <FlexRow gap={Gap.Space8}>
        <div className={style['flex-field']}>
          <FloatInput
            label={intl.formatMessage(shipToStepMessages.cityLabel)}
            onChange={(value: string) => handleField('city', value)}
            type='text'
            value={dest.city}
          />
        </div>
        <div className={style['state-field']}>
          <Text textType={TextType.Label} value={<FormattedMessage {...shipToStepMessages.stateLabel} />} />
          <StateDropdown
            componentClassName={style['dropdown-full']}
            countryCode={dest.countryCode || 'US'}
            onChange={(value: string) => handleField('state', value)}
            value={dest.state}
          />
        </div>
        <div className={style['zip-field']}>
          <FloatInput
            label={intl.formatMessage(shipToStepMessages.zipCodeLabel)}
            onChange={(value: string) => handleField('postalCode', value)}
            type='text'
            value={dest.postalCode}
          />
        </div>
      </FlexRow>

      <FloatInput
        label={intl.formatMessage(shipToStepMessages.phoneLabel)}
        onChange={(value: string) => handleField('phone', value)}
        type='text'
        value={dest.phone}
      />

      <FloatInput
        label={intl.formatMessage(shipToStepMessages.emailLabel)}
        onChange={(value: string) => handleField('email', value)}
        type='text'
        value={dest.email}
      />

      <div>
        <Text isBold textType={TextType.Caption} value={<FormattedMessage {...shipToStepMessages.deliveryServicesLabel} />} />
        <div className={style['services-grid']}>
          <Checkbox
            checked={services.isInsideDelivery}
            label={intl.formatMessage(shipToStepMessages.insideDelivery)}
            onChange={(checked: boolean) => handleServiceToggle('isInsideDelivery', checked)}
          />
          <Checkbox
            checked={services.isLiftgateDelivery}
            label={intl.formatMessage(shipToStepMessages.liftgateDelivery)}
            onChange={(checked: boolean) => handleServiceToggle('isLiftgateDelivery', checked)}
          />
          <Checkbox
            checked={services.isResidentialDelivery}
            label={intl.formatMessage(shipToStepMessages.residentialDelivery)}
            onChange={(checked: boolean) => handleServiceToggle('isResidentialDelivery', checked)}
          />
          <Checkbox
            checked={services.isConstructionSiteDelivery}
            label={intl.formatMessage(shipToStepMessages.constructionSiteDelivery)}
            onChange={(checked: boolean) => handleServiceToggle('isConstructionSiteDelivery', checked)}
          />
          <Checkbox
            checked={services.isNotifyBeforeDelivery}
            label={intl.formatMessage(shipToStepMessages.notifyBeforeDelivery)}
            onChange={(checked: boolean) => handleServiceToggle('isNotifyBeforeDelivery', checked)}
          />
          <Checkbox checked={false} label={intl.formatMessage(shipToStepMessages.holdAtTerminal)} onChange={() => undefined} />
          <Checkbox
            checked={services.isAppointmentDelivery}
            label={intl.formatMessage(shipToStepMessages.appointmentDelivery)}
            onChange={(checked: boolean) => handleServiceToggle('isAppointmentDelivery', checked)}
          />
          <Checkbox
            checked={services.isTradeShowDelivery}
            label={intl.formatMessage(shipToStepMessages.tradeShowDelivery)}
            onChange={(checked: boolean) => handleServiceToggle('isTradeShowDelivery', checked)}
          />
        </div>
      </div>

      <div className={style['step-actions']}>
        <Button onClick={onSave} theme={THEME_CONSTANTS.primary}>
          <FormattedMessage {...shipToStepMessages.saveButton} />
        </Button>
      </div>
    </FlexColumn>
  );
};
